/**
 * Step 3 — rules pre-update (owner: rules team). Phase timers, play clock + auto-snap, the snap (playCall: ▼ held
 * while snapping = FG_TOSS; Blitz reveal), hard count, rush count + RUSH_GO, conversion choice, DEAD → next
 * down/drive. Offside, the pre-snap clamp and play ends are detected after movement, in rulesPost.
 * ARCHITECTURE §5 "Football rules"; the cross-module protocol is documented in ./helpers.ts.
 */
import {
  FRAMES,
  HARD_COUNTS_PER_PLAY,
  KICK,
  MatchFormat,
  RULES,
  SPECIALS_TUNING,
  SpecialId,
  TICK_HZ,
} from '@hf/shared';
import { launchBall } from '../ball/launch';
import {
  BtnIndex,
  ConversionChoice,
  FeintKind,
  Phase,
  PlayCall,
  PlayerState,
  SpecialPhase,
  isKickCall,
} from '../enums';
import { consumePress, hasBufferedPress, isHeld, keepPress } from '../player/input';
import type { SimEventSink } from '../sink';
import type { SimState } from '../state';
import {
  chooseConversion,
  classicClockExpired,
  deadFramesFor,
  emitDownStart,
  emitDriveStart,
  emitKickoffStart,
  endHalf,
  leaveDeadBall,
  resetPlayBookkeeping,
} from './flow';
import { classicRegulation } from './helpers';

/** Classic: the game clock counts down while it runs (rules.clockRunning; see RULES.classicHalfFrames). */
const tickClassicClock = (s: SimState): void => {
  const r = s.rules;
  if (r.clockRunning && r.format === MatchFormat.CLASSIC && !r.overtime && r.gameClock > 0) r.gameClock--;
};

/**
 * Kickoff: the toss was not away within KICK.kickoffAutoFrames of the snap (or the play clock ran out): the rules
 * kick it for the kicker at KICK.autoKickCharge (a deep kick, never onside).
 */
const autoKick = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const p = s.players[r.offense as 0 | 1];
  const b = s.ball;
  if (r.passThrown || b.holder !== p.index) return;
  const c = FRAMES.throwCharge;
  p.throwCharge = c.minCharge + KICK.autoKickCharge * (c.maxCharge - c.minCharge);
  p.lob = false;
  launchBall(s, ev, p, true);
};

const rushGo = (
  s: SimState,
  ev: SimEventSink,
  reason: 'timer' | 'offense_crossed' | 'blitz' | 'turnover',
): void => {
  const r = s.rules;
  r.phase = Phase.LIVE;
  r.phaseFrame = 0;
  r.rushCount = 0;
  if (ev) ev.push({ type: 'RUSH_GO', tick: s.tick, actor: -1, reason });
};

/** The rush count ended early because the offense (or its pass) crossed the LOS. Called by rulesPost. */
export const rushGoOffenseCrossed = (s: SimState, ev: SimEventSink): void => {
  s.rules.offenseCrossed = true;
  rushGo(s, ev, 'offense_crossed');
};

/**
 * The rush count ended early because the DEFENSE took the ball (an interception behind the LOS): the return is a
 * live play at once — contacts resolve and nobody can be offside any more. Called by rulesPost.
 */
export const rushGoTurnover = (s: SimState, ev: SimEventSink): void => {
  rushGo(s, ev, 'turnover');
};

const tickRushCount = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  if (r.rushCount > 0) r.rushCount--;
  if (r.rushCount <= 0) rushGo(s, ev, r.rushSkipped ? 'blitz' : 'timer');
};

/** The play call declared with the snap (see PlayCall). */
const callAtSnap = (s: SimState): PlayCall => {
  const r = s.rules;
  if (r.playCall === PlayCall.KICKOFF) return PlayCall.KICKOFF;
  if (r.conversion === ConversionChoice.TOSS) return PlayCall.PAT_TOSS;
  if (r.conversion === ConversionChoice.TWO_POINT) return PlayCall.NORMAL;
  const h = s.players[r.offense as 0 | 1].input;
  // Classic: Special held while snapping = punt formation ("special teams"); it outranks ▼ (FG formation).
  if (classicRegulation(s) && isHeld(h, BtnIndex.S)) return PlayCall.PUNT;
  return RULES.fgFormationHoldsDown && isHeld(h, BtnIndex.D) ? PlayCall.FG_TOSS : PlayCall.NORMAL;
};

const snap = (s: SimState, ev: SimEventSink, auto: boolean): void => {
  const r = s.rules;
  const off = s.players[r.offense as 0 | 1];
  const def = s.players[(1 - r.offense) as 0 | 1];
  const call = callAtSnap(s);
  const kickoff = call === PlayCall.KICKOFF;
  const h = off.input;
  // A kickoff's Action press goes on into the charge when still held (hold = charge, release = toss); a tap only
  // starts the play. Every other snap press is used up (and the punt formation's Special press with it).
  if (!kickoff || auto || !isHeld(h, BtnIndex.A)) consumePress(h, BtnIndex.A);
  consumePress(h, BtnIndex.U);
  if (call === PlayCall.PUNT) consumePress(h, BtnIndex.S);
  const blitz = !kickoff && def.special.id === SpecialId.BLITZ && def.special.phase === SpecialPhase.ARMED;
  // A kickoff has no rush count: the receiver waits behind the restraining line until the toss is away.
  r.phase = kickoff ? Phase.LIVE : Phase.RUSH_COUNT;
  r.phaseFrame = 0;
  r.playCall = call;
  r.rushSkipped = blitz;
  r.rushCount = kickoff ? 0 : blitz ? SPECIALS_TUNING.blitz.rushFrames : RULES.rushCountFrames;
  // Classic: the clock starts with a scrimmage snap (a kick starts it when the toss is away; tries are untimed).
  r.clockRunning = !kickoff && !r.conversionPlay && classicRegulation(s);
  r.snapTick = s.tick;
  r.playCount++;
  r.offenseCrossed = false;
  r.passThrown = false;
  off.playBestX = r.los;
  def.playBestX = r.los;
  resetPlayBookkeeping(s);
  if (ev) {
    ev.push({
      type: 'SNAP',
      tick: s.tick,
      actor: r.offense,
      x: s.ball.x,
      y: s.ball.y,
      blitz,
      auto,
      playCall: call,
    });
  }
  if (kickoff && auto) autoKick(s, ev);
};

const hardCount = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const p = s.players[r.offense as 0 | 1];
  p.hardCountsUsed++;
  p.prevState = p.state;
  p.state = PlayerState.HARD_COUNT;
  p.stateFrame = 0;
  p.feintKind = FeintKind.HARD_COUNT;
  p.feintTick = s.tick;
  if (ev) ev.push({ type: 'HARD_COUNT', tick: s.tick, actor: r.offense, x: p.x, y: p.y });
};

/** Count the play clock down one frame (never below `floor`); warn on each of the last whole seconds. */
const tickPlayClock = (s: SimState, ev: SimEventSink, floor: number): void => {
  const r = s.rules;
  if (r.playClock <= floor) return;
  r.playClock--;
  if (ev && r.playClock > 0 && r.playClock % TICK_HZ === 0) {
    const sec = r.playClock / TICK_HZ;
    if (sec <= RULES.playClockWarningSec) {
      ev.push({ type: 'PLAY_CLOCK_WARNING', tick: s.tick, actor: r.offense, secondsLeft: sec });
    }
  }
};

const preSnap = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  r.phaseFrame++;
  tickPlayClock(s, ev, 0);
  // Classic: a clock still running from a tackle in bounds keeps going until the snap — and may end the half.
  tickClassicClock(s);
  if (r.clockRunning && classicClockExpired(s)) {
    endHalf(s, ev);
    return;
  }
  const h = s.players[r.offense as 0 | 1].input;
  const auto = r.playClock <= 0;
  const ready =
    r.phaseFrame >= (r.playCall === PlayCall.KICKOFF ? KICK.lineupFrames : RULES.preSnapMinFrames);
  // An Action press made before the line-up minimum (RULES.preSnapMinFrames; KICK.lineupFrames for a kickoff) is
  // held in the buffer and snaps on the first legal frame: pressing Action at any time pre-snap snaps as soon as
  // allowed (never silently dropped).
  if (!ready) keepPress(h, BtnIndex.A);
  if (auto || (ready && hasBufferedPress(h, BtnIndex.A))) {
    snap(s, ev, auto);
    return;
  }
  if (
    consumePress(h, BtnIndex.U) &&
    r.playCall !== PlayCall.KICKOFF &&
    s.players[r.offense as 0 | 1].hardCountsUsed < HARD_COUNTS_PER_PLAY
  ) {
    hardCount(s, ev);
  }
};

const conversionChoice = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  r.phaseFrame++;
  const h = s.players[r.offense as 0 | 1].input;
  if (consumePress(h, BtnIndex.U)) chooseConversion(s, ev, ConversionChoice.TWO_POINT);
  else if (consumePress(h, BtnIndex.A)) chooseConversion(s, ev, ConversionChoice.TOSS);
  else if (r.phaseFrame >= RULES.conversionChoiceFrames) chooseConversion(s, ev, ConversionChoice.TOSS);
};

export const rulesPre = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  // The match's first line-up comes from createInitialState (no sink there): announce it on the first tick.
  if (s.tick === 0 && r.phase === Phase.PRE_SNAP && r.phaseFrame === 0) {
    if (r.playCall === PlayCall.KICKOFF) emitKickoffStart(s, ev);
    else {
      emitDriveStart(s, ev);
      emitDownStart(s, ev);
    }
  }
  switch (r.phase) {
    case Phase.PRE_SNAP:
      preSnap(s, ev);
      return;
    case Phase.RUSH_COUNT:
      r.phaseFrame++;
      tickClassicClock(s);
      tickRushCount(s, ev);
      return;
    case Phase.LIVE:
      r.phaseFrame++;
      if (isKickCall(r.playCall)) {
        if (!r.passThrown && s.tick - r.snapTick >= KICK.kickoffAutoFrames && r.playCall === PlayCall.KICKOFF)
          autoKick(s, ev);
        // Classic: a kick starts the clock once the toss is away.
        if (r.passThrown && !r.clockRunning && classicRegulation(s)) r.clockRunning = true;
      }
      tickClassicClock(s);
      return;
    case Phase.DEAD:
      r.phaseFrame++;
      tickClassicClock(s); // still running after a tackle in bounds (Classic)
      if (r.phaseFrame >= deadFramesFor(s)) leaveDeadBall(s, ev);
      return;
    case Phase.CONVERSION:
      conversionChoice(s, ev);
      return;
    default:
      r.phaseFrame++;
  }
};

/**
 * Step 2 — what still runs during hit-stop (invariant 7): phase timers, the play clock, the rush count (RUSH_GO
 * can fire during a freeze) and the Classic game clock. No transitions that move anything.
 */
export const rulesDuringHitStop = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  r.phaseFrame++;
  switch (r.phase) {
    case Phase.PRE_SNAP:
      // The clock keeps running through a freeze but the auto-snap waits for the first unfrozen tick.
      tickPlayClock(s, ev, 1);
      tickClassicClock(s);
      return;
    case Phase.RUSH_COUNT:
      tickClassicClock(s);
      tickRushCount(s, ev);
      return;
    case Phase.LIVE:
    case Phase.DEAD:
      tickClassicClock(s);
      return;
    default:
  }
};
